import { useCallback, useMemo } from 'react';

import type { Group } from '@bugbug/core/types/groups';
import type { Test } from '@bugbug/core/types/tests';
import { useAppSelector } from '~/modules/store';
import { selectCurrentTestGroupsList } from '~/modules/tests/tests.selectors';
import { ACTIONS, ENTITIES, showInternalServerError } from '~/services/toasts/internalServerError';

import { testsApi } from '../../tests.api';
import { usePlaybackCursorPosition } from '../usePlaybackCursorPosition/usePlaybackCursorPosition';

export const useTestGroupPositionUpdate = () => {
  const [updateGroupsPosition, state] = testsApi.useUpdateGroupsPositionMutation();
  const groupsList = useAppSelector(selectCurrentTestGroupsList);
  const { refreshPlaybackCursorPosition } = usePlaybackCursorPosition();

  const handleUpdateGroupsPosition = useCallback(
    async ({
      testId,
      groupId,
      toIndex,
    }: {
      testId: Test['id'];
      groupId: Group['id'];
      toIndex: number;
    }) => {
      if (!groupsList) return null;

      const groupsOrder = groupsList.filter(({ id }) => id !== groupId).map(({ id }) => id);
      groupsOrder.splice(toIndex, 0, groupId);

      const newGroupsOrder = groupsOrder.map((id, index) => ({
        groupId: id,
        index,
      }));

      const result = updateGroupsPosition({
        testId,
        groupsOrder: newGroupsOrder,
      }).unwrap();

      result
        .then(() => {
          refreshPlaybackCursorPosition();
        })
        .catch((error) => {
          showInternalServerError(ENTITIES.GROUP, ACTIONS.UPDATE, error);
        });

      return result;
    },
    [groupsList, refreshPlaybackCursorPosition, updateGroupsPosition],
  );

  return useMemo(
    () => [handleUpdateGroupsPosition, state] as const,
    [handleUpdateGroupsPosition, state],
  );
};
